<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onDestroy } from 'svelte';
	import { slide } from 'svelte/transition';
	import { cubicOut } from 'svelte/easing';
	import {
		closePeekTrayOverlay,
		peekTrayOverlayOpen,
	} from 'src/components/Sidepanel/peek-tray-overlay-context';

	export let expanded = false;
	export let disabled = false;
	export let expandLabel: string | undefined;
	export let collapseLabel: string | undefined;

	let className = '';
	export { className as class };

	let dragStartY: number | null = null;
	/** Positive = finger moved up; negative = finger moved down. */
	let dragDelta = 0;
	let suppressClick = false;

	const SLIDE_MS = 220;
	const OPEN_DRAG_PX = 15;
	const CLOSE_DRAG_PX = 15;
	const CLICK_SUPPRESS_PX = 6;

	$: resolvedExpandLabel =
		expandLabel ?? $format('label_peek_tray_expand', { default: 'Show additional controls' });
	$: resolvedCollapseLabel =
		collapseLabel ?? $format('label_peek_tray_collapse', { default: 'Hide additional controls' });
	$: resolvedDisabledLabel = $format('label_peek_tray_disabled', {
		default: 'Additional controls unavailable',
	});
	$: handleLabel = disabled
		? resolvedDisabledLabel
		: expanded
			? resolvedCollapseLabel
			: resolvedExpandLabel;

	$: if (disabled && expanded) {
		expanded = false;
	}

	$: peekTrayOverlayOpen.set(disabled ? false : expanded);

	const unsubscribeOverlay = peekTrayOverlayOpen.subscribe((open) => {
		if (!open) expanded = false;
	});
	onDestroy(() => {
		unsubscribeOverlay();
		closePeekTrayOverlay();
	});

	const onHandleClick = () => {
		if (disabled) return;
		if (suppressClick) {
			suppressClick = false;
			return;
		}
		// Click opens when collapsed; when already open, click also collapses (a11y / keyboard).
		expanded = !expanded;
	};

	const onPointerDown = (event: PointerEvent) => {
		if (disabled) return;
		dragStartY = event.clientY;
		dragDelta = 0;
		suppressClick = false;
		(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
	};

	const onPointerMove = (event: PointerEvent) => {
		if (dragStartY == null) return;
		dragDelta = dragStartY - event.clientY;
		if (Math.abs(dragDelta) >= CLICK_SUPPRESS_PX) {
			suppressClick = true;
		}
	};

	const onPointerUp = () => {
		if (dragStartY == null) return;

		if (expanded) {
			// Must drag down at least CLOSE_DRAG_PX to close.
			if (dragDelta <= -CLOSE_DRAG_PX) {
				expanded = false;
			}
		} else if (dragDelta >= OPEN_DRAG_PX) {
			// Drag up to open.
			expanded = true;
		}

		dragStartY = null;
		dragDelta = 0;
	};

	const onWindowKeydown = (event: KeyboardEvent) => {
		if (disabled) return;
		if (event.key === 'Escape' && expanded) {
			expanded = false;
		}
	};
</script>

<svelte:window on:keydown={onWindowKeydown} />

<div
	class="sidepanel-peek-tray {className}"
	class:sidepanel-peek-tray--expanded={expanded}
	class:sidepanel-peek-tray--disabled={disabled}
	data-prevent-drawer-swipe
	data-prevent-sidepanel-swipe
>
	<button
		type="button"
		id="sidepanel-peek-tray-handle"
		class="sidepanel-peek-tray__handle"
		{disabled}
		aria-expanded={expanded}
		aria-label={handleLabel}
		on:click={onHandleClick}
		on:pointerdown={onPointerDown}
		on:pointermove={onPointerMove}
		on:pointerup={onPointerUp}
		on:pointercancel={onPointerUp}
	>
		<span class="sidepanel-peek-tray__grip" aria-hidden="true"></span>
		{#if $$slots.peek && !expanded}
			<span class="sidepanel-peek-tray__peek">
				<slot name="peek" />
			</span>
		{/if}
	</button>

	{#if expanded}
		<div
			class="sidepanel-peek-tray__body"
			transition:slide={{ duration: SLIDE_MS, easing: cubicOut }}
		>
			<slot />
		</div>
	{/if}
</div>

<style>
	.sidepanel-peek-tray {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		width: 100%;
		border-radius: var(--radius-m, 12px) var(--radius-m, 12px) 0 0;
		border-top: 1px solid var(--color-2, rgba(255, 255, 255, 0.16));
		background: var(--color-black, #000);
		overflow: hidden;
	}

	.sidepanel-peek-tray__handle {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-xs, 4px);
		width: 100%;
		min-height: 36px;
		padding: var(--spacing-xs, 4px) var(--spacing-s, 8px);
		border: 0;
		background: transparent;
		color: inherit;
		cursor: pointer;
		touch-action: none;
	}

	.sidepanel-peek-tray--disabled .sidepanel-peek-tray__handle {
		cursor: not-allowed;
		opacity: 0.45;
	}

	.sidepanel-peek-tray__grip {
		display: block;
		width: 40px;
		height: 4px;
		border-radius: 999px;
		background: var(--color-3, rgba(255, 255, 255, 0.35));
	}

	.sidepanel-peek-tray__peek {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		justify-content: flex-start;
		width: 100%;
		min-height: 20px;
		gap: var(--spacing-xs, 4px);
	}

	.sidepanel-peek-tray__body {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		width: 100%;
		padding: 0 var(--spacing-s, 8px) 28px;
	}
</style>
